import React, { useState, useEffect, useContext } from "react";
import { UserAuthContext } from "./context";
import { getUser, logoutUser, refreshToken } from "../services/services";

const initialValue = {
  userData: {
    name: null,
    surname: null,
    email: null,
    password: "",
    address: null,
    role: "citizen",
    routes: null,
  },
  tokens: {
    accessToken: localStorage.getItem("accessToken"),
    refreshToken: localStorage.getItem("refreshToken"),
    expiresAt: localStorage.getItem("expiresAt"),
  },
  isAuthenticated: false,
};

const UserAuthProvider = ({ children }) => {
  const [userAuth, setUserAuth] = useState(initialValue);
  const [loading, setLoading] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);
  const [loadingRefreshTokens, setLoadingRefreshTokens] = useState(false);

  const saveTokens = async (tokens) => {
    const { access_token, refresh_token, expires_in, token_type } = tokens;
    const expiresAt = new Date().getTime() + expires_in * 1000;

    localStorage.setItem("accessToken", access_token);
    localStorage.setItem("refreshToken", refresh_token);
    localStorage.setItem("expiresAt", expiresAt);

    setUserAuth((prevAuth) => ({
      ...prevAuth,
      tokens: {
        accessToken: access_token,
        refreshToken: refresh_token,
        expiresAt: expiresAt,
      },
      isAuthenticated: true,
    }));
  };

  const updateUser = (user) => {
    const tempUserAuth = userAuth;

    tempUserAuth.userData = { ...tempUserAuth.userData, ...user };

    setUserAuth(tempUserAuth);
    refreshAccessToken();
  };

  const refreshAccessToken = async () => {
    setLoadingRefreshTokens(true);

    try {
      const response = await refreshToken(userAuth.tokens.refreshToken);
      if (response) {
        // Wait for tokens to be saved before proceeding
        await saveTokens(response);
        await saveUser(response.access_token);
        setLoadingRefreshTokens(false);
      } else {
        logout();
      }
    } catch (error) {
      console.error("Error refreshing token:", error);
      logout();
      setLoadingRefreshTokens(false);
    }
  };

  const logout = async () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("expiresAt");

    setUserAuth({
      userData: null,
      tokens: {
        accessToken: null,
        refreshToken: null,
        expiresAt: null,
      },
      isAuthenticated: false,
    });
  };

  const formatRoutes = (routes) => {
    return routes.map((route) => {
      return {
        ...route,
        type: "user",
        linestring: route.linestring.map((line) => ({
          lat: line[0],
          lng: line[1],
        })),
      };
    });
  };

  const saveUser = async (token) => {
    setLoadingUser(true);
    try {
      const response = await getUser(token);
      if (response) {
        const userData = {
          name: response.first_name,
          surname: response.last_name,
          email: response.email,
          address: response.address,
          role: "citizen",
          routes: formatRoutes(response.routes),
        };
        localStorage.setItem("user", JSON.stringify(userData));

        setUserAuth((prevAuth) => ({
          ...prevAuth,
          userData,
        }));

        setLoadingUser(false);
      }
    } catch (error) {
      console.log("Error saving user", error);
      setLoadingUser(false);
    }
  };

  useEffect(() => {
    if (!userAuth.tokens.accessToken || !userAuth.tokens.expiresAt) return;

    const timeUntilExpiry = userAuth.tokens.expiresAt - new Date().getTime();
    const refreshBuffer = 5 * 60 * 1000; // 5 minutes

    if (timeUntilExpiry <= refreshBuffer) {
      refreshAccessToken();
    } else {
      setUserAuth({ ...userAuth, isAuthenticated: true });
    }

    const intervalId = setInterval(() => {
      refreshAccessToken();
    }, timeUntilExpiry - refreshBuffer);

    return () => clearInterval(intervalId);
  }, [userAuth.tokens.accessToken, userAuth.tokens.expiresAt]);

  return (
    <UserAuthContext.Provider
      value={{
        ...userAuth,
        saveTokens,
        saveUser,
        updateUser,
        logout,
        loadingUser,
        loadingRefreshTokens,
      }}
    >
      {children}
    </UserAuthContext.Provider>
  );
};

export default UserAuthProvider;

export const useAuth = () => {
  const context = useContext(UserAuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
